// Feature #262: API client for the Lab automation framework.
// snake_case ↔ camelCase mapping kept in this layer; the React UI
// consumes the camelCase types only.

import client from './client'

export type AuthProfile = 'none' | 'graph_app' | 'bearer_static' | 'internal'

export interface AutomationStep {
  id: string
  sequenceId: string
  stepOrder: number
  actionType: string
  actionConfig: Record<string, unknown>
  authProfile: AuthProfile
  responseCapture: Record<string, string> | null
  continueOnFailure: boolean
  timeoutSeconds: number
  isActive: boolean
}

export interface AutomationSequence {
  id: string
  name: string
  description: string | null
  sourceModule: string
  triggerEventType: string
  triggerFilter: Record<string, unknown> | null
  retryMaxAttempts: number
  retryBackoffSeconds: number
  isEnabled: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string | null
  steps: AutomationStep[]
}

export interface AutomationRunStep {
  id: string
  runId: string
  stepId: string
  stepOrder: number
  startedAt: string
  completedAt: string | null
  status: string
  requestSummary: string | null
  responseSummary: string | null
  capturedValues: Record<string, unknown> | null
  errorMessage: string | null
  attemptCount: number
}

export interface AutomationRun {
  id: string
  sequenceId: string
  sourceModule: string
  sourceEntityType: string
  sourceEntityId: string
  triggerEventId: string | null
  triggeredAt: string
  completedAt: string | null
  status: string
  failureReason: string | null
  replayOfRunId: string | null
  steps: AutomationRunStep[]
}

interface RawSequence {
  id: string
  name: string
  description?: string | null
  source_module: string
  trigger_event_type: string
  trigger_filter?: Record<string, unknown> | null
  retry_max_attempts: number
  retry_backoff_seconds: number
  is_enabled: boolean
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: string | null
  steps?: RawStep[]
}

interface RawStep {
  id: string
  sequence_id: string
  step_order: number
  action_type: string
  action_config: Record<string, unknown>
  auth_profile: string
  response_capture?: Record<string, string> | null
  continue_on_failure: boolean
  timeout_seconds: number
  is_active: boolean
}

interface RawRunStep {
  id: string
  run_id: string
  step_id: string
  step_order: number
  started_at: string
  completed_at?: string | null
  status: string
  request_summary?: string | null
  response_summary?: string | null
  captured_values?: Record<string, unknown> | null
  error_message?: string | null
  attempt_count: number
}

interface RawRun {
  id: string
  sequence_id: string
  source_module: string
  source_entity_type: string
  source_entity_id: string
  trigger_event_id?: string | null
  triggered_at: string
  completed_at?: string | null
  status: string
  failure_reason?: string | null
  replay_of_run_id?: string | null
  steps?: RawRunStep[]
}

const mapStep = (s: RawStep): AutomationStep => ({
  id: s.id,
  sequenceId: s.sequence_id,
  stepOrder: s.step_order,
  actionType: s.action_type,
  actionConfig: s.action_config,
  authProfile: s.auth_profile as AuthProfile,
  responseCapture: s.response_capture ?? null,
  continueOnFailure: s.continue_on_failure,
  timeoutSeconds: s.timeout_seconds,
  isActive: s.is_active,
})

const mapSequence = (s: RawSequence): AutomationSequence => ({
  id: s.id,
  name: s.name,
  description: s.description ?? null,
  sourceModule: s.source_module,
  triggerEventType: s.trigger_event_type,
  triggerFilter: s.trigger_filter ?? null,
  retryMaxAttempts: s.retry_max_attempts,
  retryBackoffSeconds: s.retry_backoff_seconds,
  isEnabled: s.is_enabled,
  isActive: s.is_active,
  createdAt: s.created_at,
  updatedAt: s.updated_at,
  createdBy: s.created_by ?? null,
  steps: (s.steps || []).map(mapStep),
})

const mapRunStep = (s: RawRunStep): AutomationRunStep => ({
  id: s.id,
  runId: s.run_id,
  stepId: s.step_id,
  stepOrder: s.step_order,
  startedAt: s.started_at,
  completedAt: s.completed_at ?? null,
  status: s.status,
  requestSummary: s.request_summary ?? null,
  responseSummary: s.response_summary ?? null,
  capturedValues: s.captured_values ?? null,
  errorMessage: s.error_message ?? null,
  attemptCount: s.attempt_count,
})

const mapRun = (r: RawRun): AutomationRun => ({
  id: r.id,
  sequenceId: r.sequence_id,
  sourceModule: r.source_module,
  sourceEntityType: r.source_entity_type,
  sourceEntityId: r.source_entity_id,
  triggerEventId: r.trigger_event_id ?? null,
  triggeredAt: r.triggered_at,
  completedAt: r.completed_at ?? null,
  status: r.status,
  failureReason: r.failure_reason ?? null,
  replayOfRunId: r.replay_of_run_id ?? null,
  steps: (r.steps || []).map(mapRunStep),
})

interface PaginatedSequences {
  items: RawSequence[]
  total: number
}
interface PaginatedRuns {
  items: RawRun[]
  total: number
}

export interface SequenceFilters {
  sourceModule?: string
  triggerEventType?: string
  enabled?: boolean
}

export const listSequences = async (
  filters: SequenceFilters = {},
): Promise<AutomationSequence[]> => {
  const params: Record<string, string | boolean> = { page: 1, page_size: 100 } as never
  if (filters.sourceModule) params.source_module = filters.sourceModule
  if (filters.triggerEventType) params.trigger_event_type = filters.triggerEventType
  if (filters.enabled !== undefined) params.enabled = filters.enabled
  const res = await client.get<PaginatedSequences>('/automation/sequences', { params })
  return (res.data.items || []).map(mapSequence)
}

export const getSequence = async (id: string): Promise<AutomationSequence> => {
  const res = await client.get<RawSequence>(`/automation/sequences/${id}`)
  return mapSequence(res.data)
}

export interface SequenceInput {
  name: string
  description?: string | null
  sourceModule: string
  triggerEventType: string
  triggerFilter?: Record<string, unknown> | null
  retryMaxAttempts?: number
  retryBackoffSeconds?: number
  isEnabled?: boolean
}

export const createSequence = async (input: SequenceInput): Promise<AutomationSequence> => {
  const res = await client.post<RawSequence>('/automation/sequences', {
    name: input.name,
    description: input.description ?? null,
    source_module: input.sourceModule,
    trigger_event_type: input.triggerEventType,
    trigger_filter: input.triggerFilter ?? null,
    retry_max_attempts: input.retryMaxAttempts ?? 3,
    retry_backoff_seconds: input.retryBackoffSeconds ?? 60,
    is_enabled: input.isEnabled ?? false,
  })
  return mapSequence(res.data)
}

export const updateSequence = async (
  id: string,
  input: Partial<SequenceInput>,
): Promise<AutomationSequence> => {
  const body: Record<string, unknown> = {}
  if (input.name !== undefined) body.name = input.name
  if (input.description !== undefined) body.description = input.description
  if (input.sourceModule !== undefined) body.source_module = input.sourceModule
  if (input.triggerEventType !== undefined) body.trigger_event_type = input.triggerEventType
  if (input.triggerFilter !== undefined) body.trigger_filter = input.triggerFilter
  if (input.retryMaxAttempts !== undefined) body.retry_max_attempts = input.retryMaxAttempts
  if (input.retryBackoffSeconds !== undefined) body.retry_backoff_seconds = input.retryBackoffSeconds
  if (input.isEnabled !== undefined) body.is_enabled = input.isEnabled
  const res = await client.patch<RawSequence>(`/automation/sequences/${id}`, body)
  return mapSequence(res.data)
}

export const deleteSequence = async (id: string): Promise<void> => {
  await client.delete(`/automation/sequences/${id}`)
}

export interface StepInput {
  stepOrder: number
  actionType: string
  actionConfig: Record<string, unknown>
  authProfile?: AuthProfile
  responseCapture?: Record<string, string> | null
  continueOnFailure?: boolean
  timeoutSeconds?: number
}

const stepInputToBody = (input: StepInput) => ({
  step_order: input.stepOrder,
  action_type: input.actionType,
  action_config: input.actionConfig,
  auth_profile: input.authProfile ?? 'none',
  response_capture: input.responseCapture ?? null,
  continue_on_failure: input.continueOnFailure ?? false,
  timeout_seconds: input.timeoutSeconds ?? 30,
})

export const createStep = async (
  sequenceId: string,
  input: StepInput,
): Promise<AutomationStep> => {
  const res = await client.post<RawStep>(
    `/automation/sequences/${sequenceId}/steps`,
    stepInputToBody(input),
  )
  return mapStep(res.data)
}

export const updateStep = async (
  sequenceId: string,
  stepId: string,
  input: Partial<StepInput>,
): Promise<AutomationStep> => {
  const body: Record<string, unknown> = {}
  if (input.stepOrder !== undefined) body.step_order = input.stepOrder
  if (input.actionType !== undefined) body.action_type = input.actionType
  if (input.actionConfig !== undefined) body.action_config = input.actionConfig
  if (input.authProfile !== undefined) body.auth_profile = input.authProfile
  if (input.responseCapture !== undefined) body.response_capture = input.responseCapture
  if (input.continueOnFailure !== undefined)
    body.continue_on_failure = input.continueOnFailure
  if (input.timeoutSeconds !== undefined) body.timeout_seconds = input.timeoutSeconds
  const res = await client.patch<RawStep>(
    `/automation/sequences/${sequenceId}/steps/${stepId}`,
    body,
  )
  return mapStep(res.data)
}

export const deleteStep = async (sequenceId: string, stepId: string): Promise<void> => {
  await client.delete(`/automation/sequences/${sequenceId}/steps/${stepId}`)
}

export const testFire = async (
  sequenceId: string,
  payload: { sourceEntityType: string; sourceEntityId: string; payload?: Record<string, unknown> },
): Promise<{ run_ids: string[] }> => {
  const res = await client.post<{ run_ids: string[] }>(
    `/automation/sequences/${sequenceId}/test-fire`,
    {
      source_entity_type: payload.sourceEntityType,
      source_entity_id: payload.sourceEntityId,
      payload: payload.payload ?? {},
    },
  )
  return res.data
}

export const listRuns = async (sequenceId: string): Promise<AutomationRun[]> => {
  const res = await client.get<PaginatedRuns>(
    `/automation/sequences/${sequenceId}/runs`,
    { params: { page: 1, page_size: 100 } },
  )
  return (res.data.items || []).map(mapRun)
}

export const getRun = async (runId: string): Promise<AutomationRun> => {
  const res = await client.get<RawRun>(`/automation/runs/${runId}`)
  return mapRun(res.data)
}

export const replayRun = async (
  runId: string,
): Promise<{ run_id: string; replay_of_run_id: string }> => {
  const res = await client.post<{ run_id: string; replay_of_run_id: string }>(
    `/automation/runs/${runId}/replay`,
  )
  return res.data
}

export const listActions = async (): Promise<string[]> => {
  const res = await client.get<{ action_type: string }[]>('/automation/actions')
  return (res.data || []).map((a) => a.action_type)
}
